import { useCallback, useEffect, useState } from "react";
import { Mail, Sparkles, Loader2, Send, Trash2, Users, Inbox, ShieldAlert } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const PERM_LABEL = { automatic: "Sends immediately", approval: "Queues for your approval", off: "Sending is off" };

export default function MarketingEmail() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("compose");
  const [instruction, setInstruction] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendTo, setSendTo] = useState({});
  const load = useCallback(() => { api.get("/marketing/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { drafts, sent, send_permission, audience_size, audience_sample, stats } = data;

  const aiDraft = async () => {
    if (!instruction.trim()) return toast.error("Tell the AI what to write.");
    setBusy(true);
    try { await api.post("/marketing/draft", { instruction }); toast.success("AI drafted an email"); setInstruction(""); setTab("drafts"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Draft failed."); } finally { setBusy(false); }
  };
  const manualDraft = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body are required.");
    setBusy(true);
    try { await api.post("/marketing/draft", { subject, body }); toast.success("Draft saved"); setSubject(""); setBody(""); setTab("drafts"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setBusy(false); }
  };
  const send = async (id) => {
    const to = (sendTo[id] || "").trim(); if (!to) return toast.error("Enter a recipient email.");
    setBusy(true);
    try { const { data } = await api.post("/marketing/send", { draft_id: id, to }); toast.success(data.queued ? data.message : "Email sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/marketing/draft/${id}`); load(); } catch { toast.error("Could not delete."); } };
  const launch = async () => {
    setBusy(true);
    try {
      const { data: l } = await api.post("/marketing/announcements/launch");
      const { data: r } = await api.post("/marketing/send-all", { draft_id: l.draft.id });
      toast.success(r.queued ? r.message : `Announcement sent to ${r.sent} member(s)${r.failed ? `, ${r.failed} failed` : ""}`);
      setTab("drafts"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Launch announcement failed."); } finally { setBusy(false); }
  };
  const sendAll = async (id) => {
    setBusy(true);
    try { const { data: r } = await api.post("/marketing/send-all", { draft_id: id }); toast.success(r.queued ? r.message : `Sent to ${r.sent} member(s)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed."); } finally { setBusy(false); }
  };

  const permColor = send_permission === "off" ? "text-red-600 bg-red-50" : send_permission === "approval" ? "text-amber-700 bg-amber-50" : "text-emerald-700 bg-emerald-50";

  return (
    <div className="animate-fade-up space-y-6" data-testid="marketing-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Mail size={22} className="text-[#b0862f]" /> Marketing & Email</h1>
        <p className="mt-1 text-[13px] text-slate-500">Draft campaigns with your AI Email Specialist and send via Resend. Sending follows your Operating Mode permission.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Drafts" value={stats.drafts} tone="slate" />
        <Stat label="Sent" value={stats.sent} tone="green" />
        <Stat label="Audience" value={audience_size} tone="blue" />
        <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Send mode</div><div className={`mt-1 inline-block rounded-md px-2 py-0.5 text-[12px] font-bold ${permColor}`} data-testid="send-permission">{PERM_LABEL[send_permission]}</div></Card>
      </div>

      <Card className="card-spotlight flex flex-wrap items-center justify-between gap-3 p-5" data-testid="launch-announcement-card">
        <div>
          <div className="font-head text-[15px] font-bold text-slate-900">Launch announcement — Tradelines &amp; Rent &amp; Bill Reporting</div>
          <p className="mt-0.5 text-[12px] text-slate-500">One tap creates the branded draft and broadcasts it to {audience_size} opted-in member(s). {send_permission === "approval" ? "Your Operating Mode requires approval, so it will queue in Approvals first." : send_permission === "off" ? "Sending is OFF in Operating Mode." : "Sends immediately."}</p>
        </div>
        <button onClick={launch} disabled={busy || send_permission === "off"} data-testid="launch-announcement-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2.5 font-head text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {send_permission === "approval" ? "Queue launch email" : "Send launch email"}</button>
      </Card>

      <div className="flex gap-5 border-b border-slate-200">
        {[["compose", "Compose", Sparkles], ["drafts", `Drafts (${drafts.length})`, Mail], ["sent", `Sent (${sent.length})`, Send], ["audience", "Audience", Users]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`mkt-tab-${id}`}
            className={`flex items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${tab === id ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="mkt-compose">
          <Card className="p-5">
            <div className="mb-2 flex items-center gap-1.5 text-[13px] font-bold text-[#b0862f]"><Sparkles size={14} /> Draft with AI</div>
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={4} data-testid="ai-instruction" className="w-full rounded-lg border border-slate-200 p-3 text-[14px] outline-none focus:border-[#b0862f]" placeholder="e.g. Announce our new funding-readiness program to existing members, warm and concise, one clear CTA." />
            <button onClick={aiDraft} disabled={busy} data-testid="ai-draft-btn" className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#b0862f] px-4 py-2 text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate draft</button>
          </Card>
          <Card className="p-5">
            <div className="mb-2 text-[13px] font-bold text-slate-800">Write it yourself</div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="manual-subject" placeholder="Subject" className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px] outline-none focus:border-[#1a5fd0]" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="manual-body" placeholder="Email body (Markdown)…" className="w-full rounded-lg border border-slate-200 p-3 text-[13px] outline-none focus:border-[#1a5fd0]" />
            <button onClick={manualDraft} disabled={busy} data-testid="manual-draft-btn" className="mt-2 rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">Save draft</button>
          </Card>
        </div>
      )}

      {tab === "drafts" && (
        <div className="space-y-3" data-testid="mkt-drafts">
          {drafts.length === 0 ? <Card className="p-8 text-center text-[13px] text-slate-400">No drafts yet. Compose one above.</Card> : drafts.map((d) => (
            <Card key={d.id} className="p-4" data-testid="draft-row">
              <div className="flex items-center justify-between gap-2">
                <div className="font-head text-[14px] font-bold text-slate-900">{d.subject}</div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">{d.status}</span>
              </div>
              <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-[13px] text-slate-500">{d.body}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input value={sendTo[d.id] || ""} onChange={(e) => setSendTo({ ...sendTo, [d.id]: e.target.value })} data-testid="send-to" placeholder="recipient@email.com" className="rounded-lg border border-slate-200 px-3 py-1.5 text-[12px] outline-none focus:border-[#1a5fd0]" />
                <button onClick={() => send(d.id)} disabled={busy} data-testid="send-draft-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-700 disabled:opacity-60"><Send size={13} /> {send_permission === "approval" ? "Queue send" : "Send"}</button>
                <button onClick={() => sendAll(d.id)} disabled={busy} data-testid="send-all-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"><Users size={13} /> All members</button>
                <button onClick={() => del(d.id)} className="ml-auto text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "sent" && (
        <div className="space-y-2" data-testid="mkt-sent">
          {sent.length === 0 ? <Card className="p-8 text-center text-[13px] text-slate-400">No emails sent yet.</Card> : sent.map((s) => (
            <Card key={s.id} className="flex items-center justify-between gap-3 p-4"><div><div className="font-head text-[14px] font-bold text-slate-900">{s.subject}</div><div className="font-mono text-[10px] text-slate-400">to {s.to} · {String(s.created_at).slice(0, 16).replace("T", " ")}</div></div><span className="rounded-full bg-emerald-50 px-2.5 py-1 font-mono text-[9px] font-bold uppercase text-emerald-600">{s.status}</span></Card>
          ))}
        </div>
      )}

      {tab === "audience" && (
        <Card className="p-5" data-testid="mkt-audience">
          <div className="mb-3 flex items-center gap-2"><Users size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Opted-in members ({audience_size})</span></div>
          {audience_sample.length === 0 ? <div className="py-4 text-center text-[13px] text-slate-400">No opted-in members yet.</div> : (
            <div className="space-y-1.5">{audience_sample.map((a, i) => (<div key={i} className="flex items-center justify-between text-[13px]"><span className="text-slate-700">{a.name || a.email}</span><span className="font-mono text-[11px] text-slate-400">{a.company || ""} · {a.email}</span></div>))}</div>
          )}
        </Card>
      )}
    </div>
  );
}
